/**
 * Quote Modal Web Component
 * Creates draft orders and adds items to cart
 *
 * 2026-09-15: submit goes through window.WHL_Attribution.submitQuote (the
 * whl-quote-request Supabase edge function, with attribution). The legacy
 * Gadget createDraftOrder call is gone from this file. No Gadget fallback
 * (owner ruling 2026-09-15); a failed submit shows the existing error dialog.
 */

class QuoteModal extends HTMLElement {
  constructor() {
    super();
    this.STORAGE_KEY = 'sms_quote_cart';
    this.CONTACT_STORAGE_KEY = 'sms_quote_contact_info';
  }

  connectedCallback() {
    // Don't render yet - wait for show() to be called
  }

  disconnectedCallback() {
    // Cleanup when removed
    this.removeEventListeners();
  }

  render() {
    this.innerHTML = `
      <div class="quote-cart-quote-modal">
        <div class="quote-cart-quote-modal-content">
          <h2>Create Quote Request</h2>
          <form id="draft-quote-form-single">
            <div class="form-group">
              <label for="quote-first-name-single">First Name <span style="color: red;">*</span></label>
              <input type="text" id="quote-first-name-single" required>
            </div>
            <div class="form-group">
              <label for="quote-last-name-single">Last Name <span style="color: red;">*</span></label>
              <input type="text" id="quote-last-name-single" required>
            </div>
            <div class="form-group">
              <label for="quote-email-single">Email <span style="color: red;">*</span></label>
              <input type="email" id="quote-email-single" required>
            </div>
            <div class="quote-cart-quote-modal-actions">
              <button type="button" class="button--secondary cancel-btn">Cancel</button>
              <button type="submit" class="button--primary">Submit Quote</button>
            </div>
          </form>
        </div>
      </div>
    `;

    // Auto-fill contact info from localStorage if available
    try {
      const storedContact = localStorage.getItem(this.CONTACT_STORAGE_KEY);
      if (storedContact) {
        const contactInfo = JSON.parse(storedContact);

        const firstNameInput = this.querySelector('#quote-first-name-single');
        const lastNameInput = this.querySelector('#quote-last-name-single');
        const emailInput = this.querySelector('#quote-email-single');

        if (firstNameInput && contactInfo.firstName) {
          firstNameInput.value = contactInfo.firstName;
        }
        if (lastNameInput && contactInfo.lastName) {
          lastNameInput.value = contactInfo.lastName;
        }
        if (emailInput && contactInfo.email) {
          emailInput.value = contactInfo.email;
        }
      }
    } catch (error) {
      console.error('Error retrieving stored contact info:', error);
    }
  }

  attachEventListeners() {
    this.form = this.querySelector('#draft-quote-form-single');
    this.cancelBtn = this.querySelector('.cancel-btn');
    this.overlay = this.querySelector('.quote-cart-quote-modal');

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);

    if (this.form) this.form.addEventListener('submit', this.handleSubmit);
    if (this.cancelBtn) this.cancelBtn.addEventListener('click', this.handleCancel);
    if (this.overlay) this.overlay.addEventListener('click', this.handleOverlayClick);

    // Add change listeners to update storage when fields are modified
    const firstNameInput = this.querySelector('#quote-first-name-single');
    const lastNameInput = this.querySelector('#quote-last-name-single');
    const emailInput = this.querySelector('#quote-email-single');

    if (firstNameInput) firstNameInput.addEventListener('blur', this.handleFieldChange);
    if (lastNameInput) lastNameInput.addEventListener('blur', this.handleFieldChange);
    if (emailInput) emailInput.addEventListener('blur', this.handleFieldChange);
  }

  handleFieldChange() {
    // Update storage whenever a field is changed
    const firstName = this.querySelector('#quote-first-name-single')?.value || '';
    const lastName = this.querySelector('#quote-last-name-single')?.value || '';
    const email = this.querySelector('#quote-email-single')?.value || '';

    if (firstName || lastName || email) {
      this.saveContactInfo(firstName, lastName, email);
    }
  }

  saveContactInfo(firstName, lastName, email) {
    try {
      const contactInfo = { firstName, lastName, email };
      localStorage.setItem(this.CONTACT_STORAGE_KEY, JSON.stringify(contactInfo));
    } catch (error) {
      console.error('Error saving contact info:', error);
    }
  }

  removeEventListeners() {
    if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
    if (this.cancelBtn) this.cancelBtn.removeEventListener('click', this.handleCancel);
    if (this.overlay) this.overlay.removeEventListener('click', this.handleOverlayClick);
  }

  handleCancel() {
    this.close();
  }

  handleOverlayClick(e) {
    if (e.target === this.overlay) {
      this.close();
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    const firstName = this.querySelector('#quote-first-name-single').value;
    const lastName = this.querySelector('#quote-last-name-single').value;
    const email = this.querySelector('#quote-email-single').value;

    // Store contact info for future use
    this.saveContactInfo(firstName, lastName, email);

    const submitButton = this.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = 'Submitting...';

    try {
      // Get item data from component attribute
      const itemData = this.itemData;
      if (!itemData) {
        throw new Error('No item data provided');
      }

      // Calculate total price
      const totalPrice = (itemData.price || 0) * (itemData.quantity || 1);

      if (!window.WHL_Attribution || !window.WHL_Attribution.submitQuote) {
        throw new Error('Quote submit helper not loaded');
      }

      // Submit through the shared helper (whl-quote-request edge function).
      // Tags and attributes match the legacy Gadget payload exactly so nothing
      // downstream (sales filters, HubSpot workflow) changes.
      await window.WHL_Attribution.submitQuote({
        source: 'modal',
        contact: { email: email, first_name: firstName, last_name: lastName },
        items: [{
          variant_id: itemData.variantId,
          quantity: itemData.quantity || 1,
          properties: itemData.properties || {},
          platform: (itemData.configuration && itemData.configuration.title) || ''
        }],
        tags: ['configurator-quote', 'single-item-quote'],
        attributes: {
          _source: 'Design Lab',
          _total_price: totalPrice.toString(),
          _item_count: '1',
          _total_quantity: (itemData.quantity || 1).toString()
        },
        total_cents: totalPrice
      });

      // Add item to localStorage cart
      this.addItemToCart(itemData);

      // Close modal first
      this.close();

      // Show success dialog
      if (window.QuoteSuccessDialog && window.SMS_QUOTE_SETTINGS) {
        window.QuoteSuccessDialog.show({
          title: window.SMS_QUOTE_SETTINGS.submittedTitle,
          message: window.SMS_QUOTE_SETTINGS.submittedMessage,
          buttonText: 'View Quote Cart',
          buttonUrl: '/pages/quote-cart'
        });
      }

    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Failed to submit quote request. Please try again or contact support.');
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  }

  addItemToCart(item) {
    const cart = this.getDraftCart();

    // Check if identical item exists
    const existingItem = cart.items.find(cartItem => {
      if (cartItem.variantId !== item.variantId) return false;

      const props1 = cartItem.properties || {};
      const props2 = item.properties || {};
      const keys1 = Object.keys(props1).sort();
      const keys2 = Object.keys(props2).sort();

      if (keys1.length !== keys2.length) return false;

      for (let key of keys1) {
        if (props1[key] !== props2[key]) return false;
      }

      return true;
    });

    if (existingItem) {
      existingItem.quantity = (existingItem.quantity || 1) + (item.quantity || 1);
    } else {
      const itemId = `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      const newItem = {
        id: itemId,
        ...item,
        addedAt: new Date().toISOString(),
      };
      cart.items.push(newItem);
    }

    this.saveDraftCart(cart);

    // Dispatch event for icon update
    document.dispatchEvent(new CustomEvent('quote-cart-updated', {
      detail: { cart, action: 'add', item }
    }));
  }

  getDraftCart() {
    try {
      const cartData = localStorage.getItem(this.STORAGE_KEY);
      return cartData ? JSON.parse(cartData) : { items: [], createdAt: new Date().toISOString() };
    } catch (error) {
      console.error('Error reading quote cart:', error);
      return { items: [], createdAt: new Date().toISOString() };
    }
  }

  saveDraftCart(cart) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving quote cart:', error);
    }
  }

  close() {
    this.remove();
  }

  // Public method to show modal with item data
  show(itemData) {
    this.itemData = itemData;
    // Now render the modal
    this.render();
    this.attachEventListeners();
  }
}

// Define the custom element
customElements.define('quote-modal', QuoteModal);

// Expose global API for convenience
window.SMS_QuoteModal = {
  showForSingleItem: function(item) {
    // Prevent multiple modals - check if one already exists
    const existingModal = document.querySelector('quote-modal');
    if (existingModal) {
      console.warn('Quote modal already open');
      return;
    }

    const modal = document.createElement('quote-modal');
    document.body.appendChild(modal);
    modal.show(item);
  }
};
